class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('[data-cart-notification-close]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.notification.addEventListener('click', this.onNotificationClick.bind(this));
    this.setupTrack = this.querySelector('[data-setup-track]');
    this.setupProgress = this.querySelector('[data-setup-progress]');
    this.setupTrack?.addEventListener('scroll', () => this.updateSetupProgress(), { passive: true });
  }

  open() {
    this.notification.classList.add('animate', 'active');
    this.syncSetupProducts();
    this.updateSetupProgress();

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.hideSetupPicker();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.hideSetupPicker();
    this.cartItemKey = this.getCartItemKey(parsedState);
    this.cartItemVariantId = this.getCartItemVariantId(parsedState);
    this.cartItemProductId = String(parsedState?.product_id || '');
    this.addedItemKeys = Array.isArray(parsedState?.addedItemKeys)
      ? parsedState.addedItemKeys.map(String)
      : this.addedItemKeys || [];
    this.addedVariantIds = Array.isArray(parsedState?.addedVariantIds)
      ? parsedState.addedVariantIds.map(String)
      : this.addedVariantIds || [];

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionHtml = parsedState?.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const innerHTML =
        section.id === 'cart-notification-product'
          ? this.getAddedCartItemsHTML(parsed)
          : this.getSectionInnerHTML(parsed, section.selector);
      if (innerHTML == null) return;
      sectionElement.innerHTML = innerHTML;
    });

    this.updateAddedHeading(this.querySelectorAll('#cart-notification-product .cart-item').length);
    if (this.header) this.header.reveal();
    this.open();
  }

  getCartItemKey(parsedState) {
    if (parsedState?.key) return parsedState.key;
    if (Array.isArray(parsedState?.addedItemKeys) && parsedState.addedItemKeys[0]) {
      return parsedState.addedItemKeys[0];
    }
    if (parsedState?.token || typeof parsedState?.item_count === 'number') return '';

    const items = Array.isArray(parsedState?.items) ? parsedState.items : [];
    const mainItem = items.find((item) => !this.isAddonCartItem(item));
    return mainItem?.key || '';
  }

  getCartItemVariantId(parsedState) {
    if (parsedState?.variant_id) return String(parsedState.variant_id);
    if (parsedState?.id && !Array.isArray(parsedState?.items)) return String(parsedState.id);

    const items = Array.isArray(parsedState?.items) ? parsedState.items : [];
    const mainItem = items.find((item) => !this.isAddonCartItem(item));
    return String(mainItem?.variant_id || mainItem?.id || parsedState?.id || '');
  }

  isAddonCartItem(item) {
    const properties = item?.properties || {};
    return (
      properties._upgrade === 'true' ||
      properties._gift === 'true' ||
      properties._bms_gift === 'true' ||
      properties._theme_gift === 'true' ||
      properties._protection_plan === 'true' ||
      properties._bundle_save === 'true'
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(parsed, selector = '.shopify-section') {
    const selected = (selector && parsed.querySelector(selector)) || parsed.querySelector('.shopify-section');
    return selected?.innerHTML ?? null;
  }

  getCartItemNodeKey(item) {
    return String(item.dataset.cartKey || item.id.replace(/^cart-notification-product-/, ''));
  }

  getAddedCartItemsHTML(parsed) {
    const items = Array.from(parsed.querySelectorAll('.cart-item'));
    if (!items.length) return '';

    const addedKeys = new Set(this.addedItemKeys || []);
    const addedVariantIds = new Set(this.addedVariantIds || []);
    const mainKey = String(this.cartItemKey || '');
    const mainVariantId = String(this.cartItemVariantId || '');

    const matched = items.filter((item) => {
      const itemKey = this.getCartItemNodeKey(item);
      const variantId = String(item.dataset.variantId || '');
      const parentVariantId = String(item.dataset.parentVariantId || '');
      const isAddon = item.dataset.cartAddon === 'true';

      if (addedKeys.has(itemKey) || (mainKey && itemKey === mainKey)) return true;
      if (!addedVariantIds.has(variantId)) return false;
      if (!isAddon) return true;
      if (parentVariantId) return parentVariantId === mainVariantId;
      return true;
    });

    return this.sortAddedCartItems(matched, mainKey, mainVariantId)
      .map((item) => item.outerHTML)
      .join('');
  }

  sortAddedCartItems(items, mainKey, mainVariantId) {
    return items.slice().sort((first, second) => {
      const firstIsMain = this.isMainCartItem(first, mainKey, mainVariantId);
      const secondIsMain = this.isMainCartItem(second, mainKey, mainVariantId);
      if (firstIsMain && !secondIsMain) return -1;
      if (!firstIsMain && secondIsMain) return 1;
      return 0;
    });
  }

  isMainCartItem(item, mainKey, mainVariantId) {
    const itemKey = this.getCartItemNodeKey(item);
    if (mainKey && itemKey === mainKey) return true;
    return item.dataset.cartAddon !== 'true' && String(item.dataset.variantId || '') === String(mainVariantId);
  }

  updateAddedHeading(count) {
    const heading = this.querySelector('[data-added-heading]');
    if (!heading) return;
    const template = heading.dataset.addedTemplate || '';
    heading.textContent = template.replace('__N__', String(count || 0));
  }

  syncSetupProducts() {
    const setup = this.querySelector('[data-setup]');
    if (!setup) return;

    const addedProductIds = new Set(
      Array.from(this.querySelectorAll('#cart-notification-product .cart-item'))
        .map((item) => item.dataset.productId)
        .filter(Boolean)
    );

    this.querySelectorAll('[data-setup-item]').forEach((item) => {
      item.hidden = addedProductIds.has(String(item.dataset.productId));
    });

    const visibleItems = Array.from(this.querySelectorAll('[data-setup-item]')).filter((item) => !item.hidden);
    setup.hidden = visibleItems.length === 0;
    const progress = this.querySelector('.cart-notification__setup-progress');
    if (progress) progress.hidden = visibleItems.length <= 2;
    this.updateSetupProgress();
  }

  updateSetupProgress() {
    if (!this.setupTrack || !this.setupProgress) return;
    const maxScroll = this.setupTrack.scrollWidth - this.setupTrack.clientWidth;
    const progress = maxScroll > 0 ? this.setupTrack.scrollLeft / maxScroll : 0;
    this.setupProgress.style.width = `${Math.max(50, 50 + progress * 50)}%`;
  }

  onNotificationClick(event) {
    if (event.target.closest('[data-setup-picker-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.hideSetupPicker();
      return;
    }

    const optionButton = event.target.closest('[data-setup-picker-option]');
    if (optionButton) {
      event.preventDefault();
      event.stopPropagation();
      this.selectSetupOption(optionButton);
      return;
    }

    const submitButton = event.target.closest('[data-setup-picker-submit]');
    if (submitButton) {
      event.preventDefault();
      event.stopPropagation();
      this.submitSetupPicker(submitButton);
      return;
    }

    const addButton = event.target.closest('[data-setup-add]');
    if (!addButton || addButton.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.openSetupPicker(addButton);
  }

  readSetupProduct(item) {
    const node = item?.querySelector('[data-setup-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  openSetupPicker(button) {
    const item = button.closest('[data-setup-item]');
    const product = this.readSetupProduct(item);
    if (!product || item?.dataset.unavailable === 'true') return;

    const options = this.getPickerOptions(product);
    if (!options.length) {
      const variantId = Number(product.variants?.find((variant) => variant.available)?.id || item.dataset.variantId);
      this.addSetupProduct(button, variantId);
      return;
    }

    this.setupPickerProduct = product;
    this.setupPickerItem = item;
    this.setupPickerSelection = this.getDefaultSetupSelection(product);
    this.renderSetupPicker();

    const addedView = this.querySelector('[data-added-view]');
    const picker = this.querySelector('[data-setup-picker]');
    if (addedView) addedView.hidden = true;
    if (picker) picker.hidden = false;
    this.notification.classList.add('is-picker-open');
    picker?.querySelector('[data-setup-picker-close]')?.focus();
  }

  hideSetupPicker() {
    const addedView = this.querySelector('[data-added-view]');
    const picker = this.querySelector('[data-setup-picker]');
    if (picker) picker.hidden = true;
    if (addedView) addedView.hidden = false;
    this.notification.classList.remove('is-picker-open');
    this.setupPickerProduct = null;
    this.setupPickerItem = null;
    this.updateSetupProgress();
  }

  getPickerOptions(product) {
    const preferred = ['feel', 'feeling', 'size'];
    return [...(product?.options || [])].sort((first, second) => {
      const rank = (name) => {
        const index = preferred.indexOf(String(name || '').toLowerCase());
        return index === -1 ? preferred.length : index;
      };
      return rank(first.name) - rank(second.name);
    });
  }

  getDefaultSetupSelection(product) {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const selection = {};
    (product.options || []).forEach((option, index) => {
      selection[option.name] = variant?.options?.[index] || option.values?.[0] || '';
    });
    return selection;
  }

  findMatchingSetupVariant() {
    const product = this.setupPickerProduct;
    if (!product) return null;
    return (product.variants || []).find((variant) =>
      (product.options || []).every((option, index) => variant.options?.[index] === this.setupPickerSelection[option.name])
    );
  }

  isSetupOptionValueAvailable(optionName, value) {
    const product = this.setupPickerProduct;
    const optionIndex = (product?.options || []).findIndex((option) => option.name === optionName);
    if (optionIndex < 0) return false;

    return (product.variants || []).some((variant) => {
      if (!variant.available || variant.options?.[optionIndex] !== value) return false;
      return (product.options || []).every((option, index) => {
        if (index === optionIndex) return true;
        const selected = this.setupPickerSelection[option.name];
        return !selected || variant.options?.[index] === selected;
      });
    });
  }

  selectSetupOption(button) {
    if (button.disabled) return;
    const optionName = button.dataset.optionName;
    const optionValue = button.dataset.optionValue;
    if (!optionName) return;
    this.setupPickerSelection = {
      ...this.setupPickerSelection,
      [optionName]: optionValue,
    };
    this.renderSetupPicker();
  }

  renderSetupPicker() {
    const product = this.setupPickerProduct;
    const picker = this.querySelector('[data-setup-picker]');
    if (!product || !picker) return;

    const title = picker.querySelector('[data-setup-picker-title]');
    if (title) title.textContent = product.title || '';

    const optionsEl = picker.querySelector('[data-setup-picker-options]');
    if (optionsEl) {
      optionsEl.innerHTML = this.getPickerOptions(product)
        .map((option) => {
          const pills = (option.values || [])
            .map((value) => {
              const selected = this.setupPickerSelection[option.name] === value;
              const available = this.isSetupOptionValueAvailable(option.name, value);
              return `<button type="button" class="cart-notification__picker-pill${
                selected ? ' is-selected' : ''
              }" data-setup-picker-option data-option-name="${this.escapeHtml(option.name)}" data-option-value="${this.escapeHtml(
                value
              )}" ${available ? '' : 'disabled'}>${this.escapeHtml(value)}</button>`;
            })
            .join('');
          return `<div class="cart-notification__picker-option"><p class="cart-notification__picker-option-label">Select your ${this.escapeHtml(
            String(option.name || '').toLowerCase()
          )}:</p><div class="cart-notification__picker-pills">${pills}</div></div>`;
        })
        .join('');
    }

    this.updateSetupPickerVariant();
  }

  updateSetupPickerVariant() {
    const picker = this.querySelector('[data-setup-picker]');
    const variant = this.findMatchingSetupVariant();
    if (!picker) return;

    const image = picker.querySelector('[data-setup-picker-image]');
    const imageSrc = variant?.image || this.setupPickerProduct?.featuredImage || '';
    if (image) {
      image.hidden = !imageSrc;
      if (imageSrc) {
        image.src = imageSrc;
        image.alt = this.setupPickerProduct?.title || '';
      }
    }

    const price = picker.querySelector('[data-setup-picker-price]');
    if (price) price.textContent = variant?.priceText || '';

    const save = picker.querySelector('[data-setup-picker-save]');
    const compare = picker.querySelector('[data-setup-picker-compare]');
    const compareValue = picker.querySelector('[data-setup-picker-compare-value]');
    const hasCompare = Number(variant?.compareAtPrice || 0) > Number(variant?.price || 0);
    if (save) {
      if (hasCompare) {
        const percent = Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100);
        save.textContent = `SAVE ${percent}%`;
        save.hidden = false;
      } else {
        save.hidden = true;
      }
    }
    if (compare && compareValue) {
      compare.hidden = !hasCompare;
      compareValue.textContent = variant?.compareText || '';
    }

    const submit = picker.querySelector('[data-setup-picker-submit]');
    if (submit) submit.disabled = !variant?.available;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  async submitSetupPicker(button) {
    const variant = this.findMatchingSetupVariant();
    if (!variant?.id || !variant.available) return;
    await this.addSetupProduct(button, Number(variant.id));
  }

  async addSetupProduct(button, variantId = Number(button.closest('[data-setup-item]')?.dataset.variantId)) {
    if (!variantId) return;

    button.disabled = true;

    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections,
        sections_url: window.routes?.cart_url || '/cart',
      });

      const response = await fetch(`${window.routes.cart_add_url}`, config);
      const parsedState = await response.json();
      if (parsedState.status) throw new Error(parsedState.description || parsedState.message);

      const addedKey = this.getSetupAddedItemKey(parsedState, variantId);
      this.addedItemKeys = [...new Set([...(this.addedItemKeys || []), ...(addedKey ? [String(addedKey)] : [])])];
      this.addedVariantIds = [...new Set([...(this.addedVariantIds || []), String(variantId)])];
      this.hideSetupPicker();
      this.renderContents({
        ...parsedState,
        key: this.cartItemKey,
        variant_id: this.cartItemVariantId,
        product_id: this.cartItemProductId,
        addedItemKeys: this.addedItemKeys,
        addedVariantIds: this.addedVariantIds,
      });
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  }

  getSetupAddedItemKey(parsedState, variantId) {
    if (parsedState?.key && !Array.isArray(parsedState.items)) return parsedState.key;

    const items = Array.isArray(parsedState?.items) ? parsedState.items : [];
    const match = items.find((item) => String(item.variant_id || item.id) === String(variantId));
    if (match?.key) return match.key;

    const isFullCart = Boolean(parsedState?.token || typeof parsedState?.item_count === 'number');
    if (!isFullCart && items[0]?.key) return items[0].key;
    return '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
